// Progress: how Kenny and Cassidy are doing as they go (their pick, 2026-11-02: scoreboard A plus
// Together B). Replaces Running in the tab bar; Running is still one tap away from here and Today.
// Chart colours follow the person being shown, not whoever is signed in.
import { renderHeader } from '../components/header.js';
import { renderNav } from '../components/nav.js';
import { loadProgressData, loadExerciseLibrary, loadWeights, loadRuns } from '../lib/grit-data.js';
import { personStats, togetherStats } from '../lib/progress-stats.js';
import { twoWeekAverages } from '../lib/nutrition.js';
import { displayName, ensureNames, escapeHtml } from '../lib/exercise-names.js';
import { localISO } from '../lib/phase.js';
import { activeUser, userColor, viewing, USERS } from '../lib/auth.js';
import { icon } from '../components/icons.js';

const NAMES = { kenny: 'Kenny', cassidy: 'Cass' };
const VIEW_KEY = 'grit-progress-view';
const MINUS = '−';
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
// A big week still has to fit a third of a small phone.
const pounds = (n) => (n >= 100000 ? `${Math.round(n / 1000)}k` : n.toLocaleString('en-US'));
const signed = (n) => (n > 0 ? `+${n}` : n < 0 ? `${MINUS}${Math.abs(n)}` : '0');

function tile(value, label) {
  return `<div class="grit-card px-2.5 py-3 min-w-0"><div class="font-rounded font-bold tabular-nums text-[20px] text-ink leading-tight">${value}</div><div class="text-[13px] text-inksoft leading-tight mt-1">${label}</div></div>`;
}

function spark(series, color) {
  const W = 70, H = 24;
  const ys = series.map(([, w]) => w);
  const lo = Math.min(...ys), range = (Math.max(...ys) - lo) || 1;
  const pts = ys.map((y, i) => [4 + (i * (W - 8)) / (ys.length - 1), H - 5 - ((y - lo) / range) * (H - 10)]);
  const [ex, ey] = pts[pts.length - 1];
  return `<svg width="${W}" height="${H}" class="flex-none" aria-hidden="true"><polyline fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" points="${pts.map((p) => p.map((v) => v.toFixed(1)).join(',')).join(' ')}"/><circle cx="${ex.toFixed(1)}" cy="${ey.toFixed(1)}" r="3.5" fill="${color}" stroke="#fff" stroke-width="1.5"/></svg>`;
}

function strengthRow(g, color) {
  return `<div class="flex items-center gap-2 py-2 border-t border-hairline first:border-0 min-h-[48px]">
    <div class="flex-1 min-w-0"><div class="font-bold text-[15px] text-ink leading-tight">${escapeHtml(displayName(g.exercise) || g.exercise.name)}</div><div class="text-[13px] text-inksoft">${g.first} to ${g.last} lb</div></div>
    ${spark(g.series, color)}
    <div class="font-bold text-[15px] text-ink tabular-nums min-w-[40px] text-right">${signed(g.change)}</div>
  </div>`;
}

function strip(last14, color) {
  return `<div class="grid gap-[3px]" style="grid-template-columns:repeat(14,minmax(0,1fr))" role="img" aria-label="${last14.filter((d) => d.on).length} workouts in the last 14 days">
    ${last14.map((d) => `<div class="rounded-[5px]" style="aspect-ratio:1;background:${d.on ? color : 'rgba(118,118,128,0.12)'}" title="${shortDate(d.date)}"></div>`).join('')}
  </div>
  <div class="flex justify-between text-[13px] text-inksoft mt-1"><span>${shortDate(last14[0].date)}</span><span>Today</span></div>`;
}

function weightChart(points, color) {
  const W = 320, H = 118, L = 32, R = 12, T = 8, B = 22;
  const ys = points.map((p) => p.y);
  const lo = Math.min(...ys), hi = Math.max(...ys);
  const pad = (hi - lo) * 0.15 || 1;
  const yMin = lo - pad, yMax = hi + pad;
  const X = (i) => L + (i * (W - L - R)) / (points.length - 1);
  const Y = (y) => T + (1 - (y - yMin) / (yMax - yMin)) * (H - T - B);
  const ticks = [...new Set([Math.round(hi), Math.round(lo)])];
  const grid = ticks.map((t) => `<line x1="${L}" x2="${W - R}" y1="${Y(t).toFixed(1)}" y2="${Y(t).toFixed(1)}" stroke="rgba(60,60,67,0.12)"/><text x="${L - 6}" y="${(Y(t) + 4).toFixed(1)}" text-anchor="end" font-size="11" fill="#6E6E73">${t}</text>`).join('');
  const line = points.map((p, i) => `${X(i).toFixed(1)},${Y(p.y).toFixed(1)}`).join(' ');
  const dots = points.map((p, i) => `<circle cx="${X(i).toFixed(1)}" cy="${Y(p.y).toFixed(1)}" r="4" fill="${color}" stroke="#fff" stroke-width="2"/><circle data-wt="${i}" cx="${X(i).toFixed(1)}" cy="${Y(p.y).toFixed(1)}" r="16" fill="transparent"/>`).join('');
  return `<svg viewBox="0 0 ${W} ${H}" class="w-full mt-2" style="height:auto" role="img" aria-label="Body weight, ${points.length} weigh-ins, from ${points[0].y} to ${points[points.length - 1].y} pounds">${grid}<polyline fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" points="${line}"/>${dots}
    <text x="${L}" y="${H - 5}" font-size="11" fill="#6E6E73">${shortDate(points[0].date)}</text><text x="${W - R}" y="${H - 5}" text-anchor="end" font-size="11" fill="#6E6E73">${shortDate(points[points.length - 1].date)}</text></svg>`;
}

function duelBar(person, g, scale, color, square) {
  const mark = square ? '<rect width="10" height="10" rx="2"' : '<circle cx="5" cy="5" r="5"';
  const width = g.pct > 0 ? Math.max(3, (g.pct / scale) * 100) : 0;
  return `<div class="flex items-center gap-2 mt-1.5 text-[13px]">
    <svg width="10" height="10" class="flex-none" aria-hidden="true">${mark} fill="${color}"/></svg>
    <span class="w-[42px] flex-none text-ink">${NAMES[person]}</span>
    <span class="flex-1 h-2 rounded-full bg-fill overflow-hidden"><span class="block h-full rounded-full" style="width:${width.toFixed(0)}%;background:${color}"></span></span>
    <span class="w-[42px] flex-none text-right font-bold tabular-nums text-ink">${signed(g.pct)}%</span>
    <span class="w-[60px] flex-none text-right text-inksoft tabular-nums">${g.first} to ${g.last}</span>
  </div>`;
}

export function renderProgress(el) {
  let view = sessionStorage.getItem(VIEW_KEY) || viewing();
  let showAll = false;
  let data = null;

  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-4"><p class="text-inksoft">Loading…</p></div>${renderNav('progress')}`;

  (async () => {
    const today = localISO();
    const [library, ...people] = await Promise.all([
      loadExerciseLibrary(),
      ...USERS.map(async (u) => {
        const [activity, weights, runs] = await Promise.all([loadProgressData(u), loadWeights(u), loadRuns(u), ensureNames(u)]);
        return { user: u, activity, weights, runs };
      }),
    ]);
    data = Object.fromEntries(people.map((p) => [p.user, { ...p, stats: personStats({ ...p.activity, library, today }) }]));
    paint();
  })();

  function switcher() {
    return `<div class="grit-seg mb-3">${[...USERS.map((u) => [u, NAMES[u]]), ['together', 'Together']].map(([v, l]) => `<button data-view="${v}" class="${view === v ? 'on' : ''}">${l}</button>`).join('')}</div>`;
  }

  function personView(user) {
    const { stats, weights, runs } = data[user];
    if (!stats.days.length) {
      return `<div class="grit-card p-5"><div class="font-bold text-ink text-[17px]">Nothing yet</div><div class="text-[15px] text-inksoft mt-1">Numbers start showing here after the first finished workout.</div></div>`;
    }
    const color = userColor(user).brand;
    const changed = stats.gains.filter((g) => g.change !== 0);
    const list = showAll ? stats.gains : (changed.length ? changed : stats.gains).slice(0, 5);
    const more = stats.gains.length - list.length;
    const pts = weights.filter((w) => Number(w.weight_lb) > 0).map((w) => ({ date: w.date, y: Number(w.weight_lb) })).sort((a, b) => (a.date < b.date ? -1 : 1));
    const avgs = twoWeekAverages(weights.filter((w) => Number(w.weight_lb) > 0), localISO());
    const current = avgs[avgs.length - 1];
    const latest = pts[pts.length - 1];
    return `<div class="grid grid-cols-3 gap-2">
        ${tile(stats.workouts7, 'workouts, last 7 days')}
        ${tile(pounds(stats.volume7), 'lb moved, last 7 days')}
        ${tile(stats.heavier, 'lifts heavier than the first time')}
      </div>
      <div class="grit-card p-4 mt-3">
        <div class="font-bold text-ink text-[17px] mb-1">Getting stronger</div>
        ${stats.gains.length ? `<div>${list.map((g) => strengthRow(g, color)).join('')}</div>` : '<div class="text-[15px] text-inksoft">A lift shows up here once it has been done on two different days.</div>'}
        ${more > 0 ? `<button id="show-all" class="w-full min-h-[44px] text-[15px] font-semibold text-grit-press mt-1">Show all ${stats.gains.length} lifts</button>` : ''}
      </div>
      <div class="grit-card p-4 mt-3">
        <div class="font-bold text-ink text-[17px]">Body weight</div>
        ${pts.length ? `<div id="wt-read" class="text-[15px] text-inksoft mt-0.5">${current?.count > 1 ? `2-week average <b class="text-ink tabular-nums">${current.avg} lb</b>, last weighed ${shortDate(latest.date)}` : `<b class="text-ink tabular-nums">${latest.y} lb</b> on ${shortDate(latest.date)}`}</div>${pts.length > 1 ? weightChart(pts, color) : ''}`
    : user === activeUser() ? `<a href="#/weight" class="flex items-center justify-between min-h-[44px] text-[15px] text-inksoft">No weigh-ins yet. Add one on the Weight tab. ${icon.right('w-4 h-4 flex-none')}</a>` : '<div class="text-[15px] text-inksoft mt-1">No weigh-ins yet.</div>'}
      </div>
      <div class="grit-card p-4 mt-3">
        <div class="font-bold text-ink text-[17px] mb-2">Showing up</div>
        ${strip(stats.last14, color)}
      </div>
      ${user === viewing() ? `<a href="#/running" class="grit-card p-4 mt-3 flex items-center justify-between gap-2 min-h-[56px] active:scale-[0.99] transition-transform">
        <div><div class="font-bold text-ink text-[17px]">Running</div><div class="text-[15px] text-inksoft">${runs.length} ${runs.length === 1 ? 'run' : 'runs'} logged</div></div>
        <span class="text-inksoft flex-none">${icon.right('w-4 h-4')}</span>
      </a>` : ''}`;
  }

  function togetherView() {
    const [a, b] = USERS;
    const t = togetherStats(data[a].stats, data[b].stats);
    const [ca, cb] = [userColor(a).deep, userColor(b).deep];
    const scale = Math.max(10, ...t.shared.flatMap((s) => [s.a.pct, s.b.pct]));
    const list = showAll ? t.shared : t.shared.slice(0, 5);
    return `<div class="grid grid-cols-2 gap-2">
        ${tile(t.together, t.since ? `workouts together since ${shortDate(t.since)}` : 'workouts together')}
        ${tile(`${data[a].stats.workouts7} and ${data[b].stats.workouts7}`, `${NAMES[a]} and ${NAMES[b]}, last 7 days`)}
      </div>
      <div class="grit-card p-4 mt-3">
        <div class="font-bold text-ink text-[17px]">Getting stronger</div>
        <div class="text-[13px] text-inksoft mb-1">Percent since your first time, so you're each measured against yourself.</div>
        ${t.shared.length ? `<div>${list.map((s) => `<div class="py-2 border-t border-hairline first:border-0"><div class="font-bold text-[15px] text-ink">${escapeHtml(displayName(s.exercise) || s.exercise.name)}</div>${duelBar(a, s.a, scale, ca, false)}${duelBar(b, s.b, scale, cb, true)}</div>`).join('')}</div>` : '<div class="text-[15px] text-inksoft mt-1">A lift shows up here once you have both done it on two different days.</div>'}
        ${t.shared.length > list.length ? `<button id="show-all" class="w-full min-h-[44px] text-[15px] font-semibold text-grit-press mt-1">Show all ${t.shared.length} lifts</button>` : ''}
      </div>
      <div class="grit-card p-4 mt-3">
        <div class="font-bold text-ink text-[17px] mb-2">Showing up</div>
        <div class="text-[15px] font-semibold text-ink mb-1">${NAMES[a]}</div>${strip(data[a].stats.last14, ca)}
        <div class="text-[15px] font-semibold text-ink mt-3 mb-1">${NAMES[b]}</div>${strip(data[b].stats.last14, cb)}
      </div>`;
  }

  function paint() {
    el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-4">
      <h1 class="grit-title mb-3">Progress</h1>
      ${switcher()}
      ${view === 'together' ? togetherView() : personView(view)}
      <div class="h-24"></div>
    </div>${renderNav('progress')}`;
    el.querySelectorAll('[data-view]').forEach((b) => { b.onclick = () => { view = b.dataset.view; showAll = false; sessionStorage.setItem(VIEW_KEY, view); paint(); }; });
    const all = document.getElementById('show-all');
    if (all) all.onclick = () => { showAll = true; paint(); };
    const pts = view === 'together' ? [] : data[view].weights.filter((w) => Number(w.weight_lb) > 0).sort((a, b) => (a.date < b.date ? -1 : 1));
    el.querySelectorAll('[data-wt]').forEach((c) => {
      c.addEventListener('click', () => {
        const p = pts[Number(c.dataset.wt)];
        document.getElementById('wt-read').innerHTML = `<b class="text-ink tabular-nums">${Number(p.weight_lb)} lb</b> on ${shortDate(p.date)}`;
      });
    });
  }
}
